import {IUsageLoggingService} from "../services/IUsageLoggingService";

/**
 * [LcrAnalyticsEvent]
 *
 * By default, lcr-analytics-event will log every click on an element. This can
 * be overridden by passing in the property 'event' in the arguments to the directive.
 *
 * Example usage:
 * <!-- this button will log every click -->
 * <button lcr-analytics-event='{"category":"map control", "label":"create share"}'>
 *     Share Map
 * </button>
 *
 * <!-- this input will log every change^ in the input's value -->
 * <input lcr-analytics-event='{"event":"change", "category":"inputs", "label":"this input is logged"}'>
 *
 * <!-- this input will log every change^ in the input value, and include its value (myModel) as the event label -->
 * <input ng-model='myModel' lcr-analytics-event='{"event":"change", "category":"inputs", "label":myModel}'>
 *
 * 
 * ^ Logging the change event isn't recommended, you might be logging too much data... blocking subsequent HTTP(S)
 *		requests by filling up the request queue.
 */
export class LcrAnalyticsEvent implements ng.IDirective {
	restrict = 'A'; //attribute or element
	constructor(protected loggingService : IUsageLoggingService) {
		this.link = ($scope: ng.IScope, elem: ng.IAugmentedJQuery,
				attrs: ng.IAttributes) => {

			let parseArgs = () => {
				// $eval the arguments so events can be constructed dynamically
				// without needing to use an isolated scope on the directive.
				// this is more flexible,
				// if we use an isolated scope, the directive would
				// clash with other directives that require isolated scope.
				const argVal = $scope.$eval(attrs['lcrAnalyticsEvent']);
				return typeof argVal == 'string' ? JSON.parse(argVal) : argVal;
			};

			let args = parseArgs();
			if (!args) return;

			let jsEvent = args['event'] || 'click';

			let logEvent = (theArgs) => {
				// extract relevant properties from the logging arguments
				// and send them to the logger + analytics
				if (theArgs && loggingService) {
					let category = theArgs['category'];
					let action = theArgs['action'] || theArgs['event'] || 'click';
					let label = theArgs['label'] || '';
					const extra = theArgs['extra'] || false; // extra is a model name in the parent scope
					if ($scope.$parent && extra && $scope.$parent[extra]) {
						label += `, ${$scope.$parent[extra]}`;
					}

					let scale = theArgs['scale'];
					let layout = theArgs['layout'];

					if (scale && layout) {
                        loggingService.logWithAnalytics(category, action, label, scale, layout);
					} else {
                        loggingService.logWithAnalytics(category, action, label);
					}

				}
			};

			if (jsEvent === 'load') {
				logEvent(args);
			} else {
				elem.on(jsEvent, () => logEvent(parseArgs()));
			}
		};
	}

	link: ($scope: ng.IScope, elem: ng.IAugmentedJQuery,
		   attrs: ng.IAttributes) => void = null;

	static factory(): ng.IDirectiveFactory {
		const directive = (loggingService : IUsageLoggingService) => new LcrAnalyticsEvent(loggingService);
		directive.$inject = ['usageLoggingService'];
		return directive;
	}
}
